import { memo, useMemo } from 'react'
import useRunsheetDocumentsRowViewModel from './RunsheetDocumentsRowViewModel'
import { HUMAN_STATUSES, hasNoFile } from '@constants'
import ContentEditable from '@components/contenteditable/ContentEditable'
import { MdOutlineEdit, MdOutlineWarning } from 'react-icons/md'
import { partiesFromText } from './Runsheet.lib'
import cn from 'clsx'

const arePropsEqual = (prev, curr) => {
    
    if (Object.is(prev, curr)) return true
    
    const docId = curr.rsDocJoin?.document?.id
    const wasMarked = prev.markedDocuments.includes(docId)
    const isMarked = curr.markedDocuments.includes(docId)
    
    if (prev.rsDocJoin.locked !== curr.rsDocJoin.locked) return false
    if (wasMarked !== isMarked) return false
    
    return Object.is(prev.rsDocJoin, curr.rsDocJoin)
        && Object.is(prev.value, curr.value)
        && prev.rsDocJoin.document?.reviewStatus === curr.rsDocJoin.document.reviewStatus
    
}

const RunsheetDocumentsRow = memo(({
    rsDocJoin,
    value,
    markedDocuments,
    setMarkedDocuments,
    setEditRsDocJoin,
    showHideAdd,
    updateRunsheetValue,
}) => {
    
    const vm = useRunsheetDocumentsRowViewModel(rsDocJoin, value)
    
    const tabsText = useMemo(() => {
        return vm.tabs
            .filter(tab =>
                tab.joins?.some(join =>
                    join.runsheetDocumentValueId === rsDocJoin.runsheetDocumentValueId,
                ),
            )
            .map(tab => tab.name)
            .join('; ')
    }, [vm.tabs, rsDocJoin.runsheetDocumentValueId])
    
    const columnMappings = useMemo(() => ({
        'document.name': <td key="document.name" className="relative">
            {/* @todo cleanup all this junk styling w/Tailwind */}
            <div className="flex items-center justify-between gap-2">
                <div
                    className="max-w-[100px] truncate"
                    data-tooltip-id="rs-doc-tooltip"
                    data-tooltip-content={JSON.stringify(vm.getTooltipData(vm.document))}>
                    <span
                        className={cn({
                            'cursor-pointer': !hasNoFile(vm.document),
                        })}
                        onClick={vm.popupDocumentOrNavigate(vm.document)}>
                        {vm.document.name}
                    </span>
                    <div className="text-slate-400">
                        {HUMAN_STATUSES[vm.document.orgStatus] || HUMAN_STATUSES[vm.document.reviewStatus]}
                    </div>
                </div>
            
            </div>
        </td>,
        
        grantors: <td key="grantors"
            data-tooltip-id="default-tip"
            data-tooltip-content="Separate individual Grantors by semicolon (;) to update the list">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder={vm.placeholders.grantors[vm.currentTab?.type || 'deed']}
                grow="md"
                value={vm.applyCasing(value.grantors?.map(g => g.name).join('; ') || '')}
                onEditEnd={val =>
                    updateRunsheetValue(rsDocJoin, 'grantors')(partiesFromText(val, value.grantors))
                }
                shouldBlurOnEnter={false} />
        </td>,
        
        grantees: <td key="grantees"
            data-tooltip-id="default-tip"
            data-tooltip-content="Separate individual Grantees by semicolon (;) to update the list">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder={vm.placeholders.grantees[vm.currentTab?.type || 'deed']}
                grow="md"
                value={vm.applyCasing(value.grantees?.map(g => g.name).join('; ') || '')}
                onEditEnd={val =>
                    updateRunsheetValue(rsDocJoin, 'grantees')(partiesFromText(val, value.grantees))
                }
                shouldBlurOnEnter={false} />
        </td>,
        
        fullGrantor: <td key="fullGrantor">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder={vm.placeholders.fullGrantor[vm.currentTab?.type || 'deed']}
                grow="md"
                value={vm.applyCasing(value.fullGrantor || '')}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'fullGrantor')}
                shouldBlurOnEnter={false} />
        </td>,
        
        fullGrantee: <td key="fullGrantee">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder={vm.placeholders.fullGrantee[vm.currentTab?.type || 'deed']}
                grow="md"
                value={vm.applyCasing(value.fullGrantee || '')}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'fullGrantee')}
                shouldBlurOnEnter={false} />
        </td>,
        
        bookVolume: <td key="bookVolume" className="align-middle">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder="Add Book or Volume"
                value={value.bookVolume}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'bookVolume')}
                shouldBlurOnEnter={false} />
        </td>,
        
        bookPage: <td key="bookPage" className="align-middle">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder="Add Page"
                value={value.bookPage}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'bookPage')}
                shouldBlurOnEnter={false} />
        </td>,
        
        bookType: <td key="bookType" className="align-middle">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder="Add Book Type"
                value={value.bookType}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'bookType')}
                shouldBlurOnEnter={false} />
        </td>,
        
        instrumentNumber: <td key="instrumentNumber" className="align-middle">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder="Add Instrument #"
                value={value.instrumentNumber}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'instrumentNumber')}
                shouldBlurOnEnter={false} />
        </td>,
        
        instrumentType: <td key="instrumentType" className="align-middle">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder="Add Instrument Type"
                value={vm.applyCasing(value.instrumentType)}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'instrumentType')}
                shouldBlurOnEnter={false} />
        </td>,
        
        instrumentDate: <td key="instrumentDate" className="align-middle">
            {value.instrumentDateRaw && !value.instrumentDate && (
                <div className="pb-2 text-xs font-italic">
                    {value.instrumentDateRaw}
                </div>
            )}
            <ContentEditable
                type="datepicker"
                disabled={rsDocJoin.locked}
                placeholder="Add Instrument Date"
                value={value.instrumentDate || null}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'instrumentDate')}
                shouldBlurOnEnter={false} />
        </td>,
        
        fileDate: <td key="fileDate" className="align-middle">
            {value.fileDateRaw && !value.fileDate && (
                <div className="pb-2 text-xs font-italic">
                    {value.fileDateRaw}
                </div>
            )}
            <ContentEditable
                type="datepicker"
                disabled={rsDocJoin.locked}
                placeholder="Add File Date"
                value={value.fileDate || null}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'fileDate')}
                shouldBlurOnEnter={false} />
        </td>,
        
        recordingDate: <td key="recordingDate" className="align-middle">
            {value.recordingDateRaw && !value.recordingDate && (
                <div className="pb-2 text-xs font-italic">
                    {value.recordingDateRaw}
                </div>
            )}
            <ContentEditable
                type="datepicker"
                disabled={rsDocJoin.locked}
                placeholder="Add Recording Date"
                value={value.recordingDate || null}
                onEditEnd={updateRunsheetValue(rsDocJoin, 'recordingDate')}/>
        </td>,
        
        legalDescriptions: <td key="legalDescriptions" className="align-middle"
            onClick={() => !rsDocJoin.locked && vm.setRunsheetDocumentValue(value)}>
            <div className="max-h-[60px] overflow-auto whitespace-pre-line">
                {value.legalDescriptions
                    ?.filter(ld => ld.visible)
                    ?.map(ld =>
                        <div key={ld.id} className="pt-2">
                            {ld.summary}
                        </div>)
                }
            </div>
        </td>,
        
        comment: <td key="comments" className="align-middle">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder="Add Comment"
                value={value.comments}
                grow="sm"
                onEditEnd={updateRunsheetValue(rsDocJoin, 'comments')}
                allowsNewLine={true}
                shouldBlurOnEnter={false} />
        </td>,
        
        
        tabs: <td key="tabs" className="align-middle">
            <div className="tooltip" data-tip={tabsText}>
                <span className="line-clamp-1 w-[150px]">
                    {tabsText}
                </span>
            </div>
        </td>,
        
        
        notes: <td key="notes" className="align-middle">
            <ContentEditable
                type="textarea"
                disabled={rsDocJoin.locked}
                placeholder="Add Notes"
                value={value.notes}
                grow="sm"
                onEditEnd={updateRunsheetValue(rsDocJoin, 'notes')}
                allowsNewLine={true}
                shouldBlurOnEnter={false} />
        
        </td>,
        
    }), [vm.runsheet, rsDocJoin, value])
    
    return (<>
        
        <tr
            ref={vm.trRef}
            className="RunsheetDocumentsRow">
            
            {/* Marked documents checkbox */}
            <td className="relative">
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={markedDocuments.includes(vm.document.id)}
                        onChange={e => {
                            if (e.target.checked) {
                                setMarkedDocuments(prevMarkedDocs => [...prevMarkedDocs, vm.document.id])
                            } else {
                                setMarkedDocuments(prevMarkedDocs => prevMarkedDocs.filter(id => id !== vm.document.id))
                            }
                        }} />
                    {vm.failedOnChaining && vm.missingDataForChain
                        && <span className="tooltip tooltip-bottom-right"
                            data-tip="Missing Grantor, Grantee and/or Instrument Date">
                            <MdOutlineWarning className="text-error text-lg" />
                        </span>}
                </div>
            </td>
            
            <td className="!px-0">
                {!rsDocJoin.locked && (
                    <a
                        className="
                            flex justify-center items-center content-center
                            btn btn-outline btn-square btn-sm !p-0 bg-base-200 hover:bg-base-300"
                        data-tooltip-id="default-tip"
                        data-tooltip-content="Edit"
                        onClick={() => {
                            setEditRsDocJoin(rsDocJoin)
                            showHideAdd(true)
                        }}>
                        <MdOutlineEdit className="text-base cursor-pointer" />
                    </a>
                )}
            </td>
            
            {vm.fieldsToShow.map(f => columnMappings[f])}
        
        </tr>
        {/* useful part to test if anything goes wrong */}
        
        {/* <tr className="RunsheetDocumentsRow">
            <td colSpan={13}>
                <div>
                    <pre>
                        <code style={{ color: 'white' }}>
                            {document?.id ?? 'N/A'}
                            {' '}
                            {document?.value.length ?? '0'}
                            {' / '}
                            {JSON.stringify(document?.value, null, 4)}
                        </code>
                    </pre>
                </div>
            </td>
        </tr> */}
    
    </>)
    
}, arePropsEqual)

RunsheetDocumentsRow.displayName = 'RunsheetDocumentsRow'

export default RunsheetDocumentsRow
